import React, { useState, useEffect } from "react";
import "../assets/css/calendar.css";
import { EventItem } from "./EventAndCalendar";
import { defaultUrl } from "../features/actions";
import {
  selectRe_RenderedState,
  setSharedState,
} from "../features/sharedStateSlices/shareState";
import { useDispatch, useSelector } from "react-redux";
import { makePostRequest, makePutRequest } from "../utilities/common";

const CreateEvent = ({
  selectedDate: value,
  editMode,
  editData,
  closeModal,
}: {
  selectedDate: Date;
  editMode: boolean;
  editData: EventItem;
  closeModal: any;
}) => {
  const selectedDate = `${value.getFullYear()}-${
    value.getMonth() + 1 < 10
      ? `0${value.getMonth() + 1}`
      : value.getMonth() + 1
  }-${value.getDate() < 10 ? `0${value.getDate()}` : value.getDate()} 09`;

  const selectedDaystring = value.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const [formData, setFormData] = useState(
    editMode === true
      ? editData
      : {
          id: "",
          title: "",
          description: "",
          school_id: "",
          start_time: "09:00",
          start_date: selectedDate,
          end_date: selectedDate,
          user_type: "teacher", // Defaulting explicitly to match the initial form state choice
          time: "09:00",
          frequency: "yearly",
        },
  );

  const dispatch = useDispatch();
  const is_Rendered = useSelector((state) => selectRe_RenderedState(state));

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const eventStartingDate = formData && formData.start_date;
    const timeinHr = formData && formData.time;
    let timeCreated =
      eventStartingDate && `${eventStartingDate.slice(0, 10)} ${timeinHr}`;

    const newData = {
      id: formData.id,
      title: formData.title,
      description: formData.description,
      start_date: timeCreated,
      end_date: timeCreated,
      time: formData.time,
      start_time: formData.time,
      school_id: formData.school_id,
      created_at: timeCreated,
      updated_at: timeCreated,
      user_type: formData.user_type,
      frequency: null,
    };

    if (editMode) {
      await makePutRequest(`${defaultUrl}/schedule/${formData.id}`, newData);
      dispatch(setSharedState());
    } else {
      await makePostRequest(`${defaultUrl}/schedule/create`, newData);
    }
    closeModal();
  };

  useEffect(() => {}, [is_Rendered]);

  return (
    <div className="w-full max-w-xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
      {/* Top Banner Accent with Selected Date */}
      <div className="bg-slate-50 border-b border-slate-100 p-6 text-center">
        <span className="text-[10px] font-extrabold uppercase text-[#0096D1] tracking-wider block mb-1">
          Target Agenda Date
        </span>
        <h1 className="text-lg font-extrabold text-slate-800 tracking-tight">
          {selectedDaystring}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Field: Event Title Name */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
            Event Name
          </label>
          <input
            required
            type="text"
            name="title"
            placeholder="e.g., Parent-Teacher Association Meeting"
            value={formData.title}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, title: e.target.value }));
            }}
            className="w-full text-sm font-semibold px-4 py-3 bg-slate-50/50 text-slate-800 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all"
          />
        </div>

        {/* Field: Event Description */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
            Description Summary
          </label>
          <textarea
            required
            rows={3}
            name="description"
            placeholder="Provide a brief context or summary parameters regarding this event..."
            value={formData.description}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }));
            }}
            className="w-full text-sm font-medium px-4 py-3 bg-slate-50/50 text-slate-700 placeholder-slate-400 rounded-xl border border-slate-200 focus:outline-none focus:bg-white focus:border-[#0096D1] focus:ring-1 focus:ring-[#0096D1] transition-all resize-none leading-relaxed"
          />
        </div>

        {/* Field: Target Audience Distribution Selection */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
            Audience Visibility Target
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Option 1: Teachers Only */}
            <label
              className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer select-none transition-all ${
                formData.user_type === "teacher"
                  ? "bg-[#0096D1]/5 border-[#0096D1] text-[#0096D1]"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="seenBy"
                checked={formData.user_type === "teacher"}
                onChange={() => {
                  setFormData((prev) => ({ ...prev, user_type: "teacher" }));
                }}
                className="w-4 h-4 text-[#0096D1] border-slate-300 focus:ring-[#0096D1]"
              />
              <span className="text-xs font-bold tracking-tight">
                Teachers Only
              </span>
            </label>

            {/* Option 2: Parents and Teachers */}
            <label
              className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer select-none transition-all ${
                formData.user_type === "parent"
                  ? "bg-[#0096D1]/5 border-[#0096D1] text-[#0096D1]"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="seenBy"
                checked={formData.user_type === "parent"}
                onChange={() => {
                  setFormData((prev) => ({ ...prev, user_type: "parent" }));
                }}
                className="w-4 h-4 text-[#0096D1] border-slate-300 focus:ring-[#0096D1]"
              />
              <span className="text-xs font-bold tracking-tight">
                Parents & Teachers
              </span>
            </label>
          </div>
        </div>

        {/* Action Controls Footer Submission Grid */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={closeModal}
            className="rounded-xl px-5 py-2.5 bg-slate-50 border border-slate-200 text-slate-600 text-xs font-bold shadow-sm hover:bg-slate-100 active:scale-95 transition-all"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="min-w-[140px] inline-flex items-center justify-center rounded-xl px-5 py-2.5 bg-[#0096D1] hover:bg-[#007ba8] text-white text-xs font-bold shadow-md transform active:scale-95 transition-all"
          >
            {editMode ? "Save Configurations" : "Publish Event"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateEvent;
